import { customElement } from 'lit/decorators.js'
import maplibregl from 'maplibre-gl'
import { BaseShape } from './base-shape.js'
import { isGeoJSONSource } from '../map-engine.js'
import { addShapeLayer } from '../layer-order-manager.js'
import { db } from '../../../database/schema.js'
import { tripFeatureManager } from '../../../services/trip-feature-manager.js'
import {
  calculateMidpoint,
  updateVertexPosition,
  insertMidpointVertex,
  filterVisibleVertices,
  shouldShowMidpoint,
  deleteVerticesFromCoords,
  filterMetadataArrays,
  computeSplitSegments,
} from '../../../lib/shape-utils.js'
import { processTrack } from '../../../services/track/track-processing-service.js'

/** Marker with an attached vertex index for drag/tap identification */
interface VertexMarker extends maplibregl.Marker {
  _vertexIndex: number
}

/** Marker with an attached midpoint index for insertion identification */
interface MidpointMarker extends maplibregl.Marker {
  _midpointIndex: number
}

/** Minimum screen-space distance (px) between visible vertex handles */
const MIN_HANDLE_DISTANCE_PX = 25
/** Minimum screen-space distance (px) for a segment to get a midpoint handle */
const MIN_MIDPOINT_SEGMENT_PX = 32

@customElement('shape-line')
export class ShapeLine extends BaseShape {
  private vertexHandles: maplibregl.Marker[] = []
  private midpointHandles: maplibregl.Marker[] = []
  private draggedVertexIndex?: number | undefined
  private draggedMidpointIndex?: number | undefined
  private selectedVertexIndices: Set<number> = new Set()
  private hasDragMoved = false
  private dragStartLngLat?: maplibregl.LngLat
  private lastVertexClickShift = false
  private tracerSourceId?: string | undefined
  private tracerLayerId?: string | undefined
  // Showing the two endpoint handles for an extend end-pick (decoupled from the
  // trip-feature-manager extend state, which is no longer used for this flow).
  private _extendMode = false
  // Split-pick mode: handles stay visible but dragging is off, so a click on a vertex
  // is a tap (which picks the split point) rather than a drag.
  private _splitMode = false
  private lastMousePosition?: maplibregl.LngLat
  private _boundMouseMove?: (e: maplibregl.MapMouseEvent) => void
  private _boundZoomHandler?: (() => void) | undefined
  private cachedCleanedCoords?: GeoJSON.Position[] | undefined

  protected createLayers(): void {
    this.processTrackIfNeeded()
    const geojson = this.getGeoJSON()
    this.map.addSource(this.sourceId, { type: 'geojson', data: geojson })

    const lineWidth = this.feature.style?.lineWidth ?? 3
    const hitAreaWidth = Math.max(lineWidth * 2, 12)

    const hitLayerId = `${this.sourceId}-hit`
    addShapeLayer(this.map, {
      id: hitLayerId,
      type: 'line',
      source: this.sourceId,
      paint: {
        'line-color': 'transparent',
        'line-width': hitAreaWidth,
        'line-opacity': 0,
      },
    })

    const lineLayerId = `${this.sourceId}-line`
    const dashArray = this.feature.style?.lineDashArray ?? undefined
    addShapeLayer(this.map, {
      id: lineLayerId,
      type: 'line',
      source: this.sourceId,
      paint: {
        'line-color': this.feature.style?.lineColor ?? '#3f5a7a',
        'line-width': this.feature.style?.lineWidth ?? 3,
        'line-opacity': this.feature.style?.lineOpacity ?? 0.8,
        ...(dashArray ? { 'line-dasharray': dashArray } : {}),
      },
    })

    this.layerIds = [hitLayerId, lineLayerId]
  }

  override refreshStyle(): void {
    const lineLayerId = `${this.sourceId}-line`
    if (this.map.getLayer(lineLayerId)) {
      this.map.setPaintProperty(
        lineLayerId,
        'line-color',
        this.feature.style?.lineColor ?? '#3f5a7a'
      )
      this.map.setPaintProperty(lineLayerId, 'line-width', this.feature.style?.lineWidth ?? 3)
      this.map.setPaintProperty(lineLayerId, 'line-opacity', this.feature.style?.lineOpacity ?? 0.8)
      const dashArray = this.feature.style?.lineDashArray
      this.map.setPaintProperty(lineLayerId, 'line-dasharray', dashArray?.length ? dashArray : null)
    }
  }

  private processTrackIfNeeded(): void {
    if (this.feature.type !== 'track' || this.cachedCleanedCoords) return
    const props = this.feature.properties
    const coords = (this.feature.geometry as GeoJSON.LineString).coordinates
    if (!props?.coordTimes || !props?.coordAccuracies || coords.length < 3) return
    const processed = processTrack(
      coords,
      props.coordTimes as number[],
      props.coordAccuracies as (number | null)[],
      (props.coordActivities as (string | null)[] | undefined) ?? coords.map(() => null),
      (props.coordSpeeds as (number | null)[] | undefined) ?? coords.map(() => null),
      (props.coordDemElevations as (number | null)[] | undefined) ?? coords.map(() => null)
    )
    this.cachedCleanedCoords = processed.cleanedCoords
  }

  getGeoJSON(): GeoJSON.Feature {
    const coordinates =
      this.cachedCleanedCoords ?? (this.feature.geometry as GeoJSON.LineString).coordinates
    return {
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates },
    }
  }

  updateSource(): void {
    const source = this.map.getSource(this.sourceId)
    if (isGeoJSONSource(source)) {
      source.setData(this.getGeoJSON())
    }

    if (
      tripFeatureManager.isExtending() &&
      tripFeatureManager.getExtendingFeatureId() === this.feature.id
    ) {
      this.showEndpointHandlesOnly()
      if (tripFeatureManager.isActivelyExtending()) {
        this.updateTracerLine()
      }
    }
  }

  protected createHandles(): void {
    this.vertexHandles = []
    this.midpointHandles = []

    // Only create handles for vertices that are in-view and spaced apart
    this.rebuildVisibleHandles()

    // Rebuild on zoom/move end (not every frame)
    if (!this._boundZoomHandler) {
      this._boundZoomHandler = () => this.rebuildVisibleHandles()
      this.map.on('zoomend', this._boundZoomHandler)
      this.map.on('moveend', this._boundZoomHandler)
    }
  }

  /**
   * Rebuild vertex and midpoint handles from scratch.
   * Vertex handles: only for vertices within viewport and spaced ≥ MIN_HANDLE_DISTANCE_PX apart.
   * Midpoint handles: only between consecutive visible vertices whose segment is ≥ MIN_MIDPOINT_SEGMENT_PX.
   */
  private rebuildVisibleHandles(): void {
    // Remove existing handles
    this.vertexHandles.forEach(h => h.remove())
    this.midpointHandles.forEach(h => h.remove())
    this.vertexHandles = []
    this.midpointHandles = []
    this.handles = []

    const coords = (this.feature.geometry as GeoJSON.LineString).coordinates
    if (coords.length === 0) {
      return
    }

    const b = this.map.getBounds()
    const bounds = {
      west: b.getWest(),
      east: b.getEast(),
      south: b.getSouth(),
      north: b.getNorth(),
    }

    const visibleEntries = filterVisibleVertices(
      coords,
      bounds,
      lngLat => this.map.project(lngLat),
      MIN_HANDLE_DISTANCE_PX
    )

    // Create vertex handles
    for (const entry of visibleEntries) {
      const coord = coords[entry.index]
      const handle = this.createHandle([coord[0], coord[1]], 'vertex')
      ;(handle as VertexMarker)._vertexIndex = entry.index
      this.vertexHandles.push(handle)
      this.handles.push(handle)

      if (this.selectedVertexIndices.has(entry.index)) {
        this.applySelectedVertexStyle(handle)
      }
    }

    // Create midpoint handles between consecutive visible vertices with enough spacing
    for (let i = 0; i < visibleEntries.length - 1; i++) {
      const a = visibleEntries[i]
      const bEntry = visibleEntries[i + 1]
      if (!shouldShowMidpoint(a.px, bEntry.px, MIN_MIDPOINT_SEGMENT_PX)) {
        continue
      }

      const midCoord = calculateMidpoint(coords[a.index], coords[bEntry.index])
      const midHandle = this.createHandle(midCoord, 'midpoint')
      // Store the index of the vertex BEFORE the midpoint (for insertion)
      ;(midHandle as MidpointMarker)._midpointIndex = a.index
      this.midpointHandles.push(midHandle)
      this.handles.push(midHandle)
    }
  }

  protected override setupEventListeners(): void {
    document.addEventListener('shape-selected', this._boundHandleOtherShapeSelected)

    const hitLayerId = `${this.sourceId}-hit`

    // Tap routing (identify / select) is owned by GestureController; the shape
    // only keeps its hover affordance on the hit layer.
    this.map.on('mouseenter', hitLayerId, () => {
      this.map
        .getCanvas()
        .style.setProperty('cursor', this.isEditing ? 'move' : 'pointer', 'important')
    })
    this.map.on('mouseleave', hitLayerId, () => {
      this.map.getCanvas().style.removeProperty('cursor')
    })

    this._boundMouseMove = (e: maplibregl.MapMouseEvent) => {
      this.lastMousePosition = e.lngLat
      if (
        tripFeatureManager.isActivelyExtending() &&
        tripFeatureManager.getExtendingFeatureId() === this.feature.id
      ) {
        this.updateTracerLine()
      }
    }
    this.map.on('mousemove', this._boundMouseMove)
  }

  private _boundHandleOtherShapeSelected = (event: Event): void => {
    const customEvent = event as CustomEvent
    if (customEvent.detail.shapeId !== this.feature.id) {
      this.exitEditMode()
    }
  }

  protected override removeEventListeners(): void {
    document.removeEventListener('shape-selected', this._boundHandleOtherShapeSelected)
    this.layerIds.forEach(layerId => {
      const mapObj = this.map as unknown as { off(type: string, layerId: string): void }
      mapObj.off('click', layerId)
      mapObj.off('mouseenter', layerId)
      mapObj.off('mouseleave', layerId)
    })
    if (this._boundMouseMove) {
      this.map.off('mousemove', this._boundMouseMove)
    }
  }

  protected override setupHandleDragging(marker: maplibregl.Marker, handleType: string): void {
    const element = marker.getElement()

    const initDrag = (): void => {
      // Split-pick mode: dragging off so a vertex click is a tap (picks the split point).
      if (this._splitMode) return
      // In extend mode, don't allow dragging — only endpoint clicks
      if (
        tripFeatureManager.isExtending() &&
        tripFeatureManager.getExtendingFeatureId() === this.feature.id
      ) {
        return
      }
      this.hasDragMoved = false
      this.dragStartLngLat = marker.getLngLat()
      if (handleType === 'vertex') {
        this.draggedVertexIndex = (marker as VertexMarker)._vertexIndex
      } else if (handleType === 'midpoint') {
        this.draggedMidpointIndex = (marker as MidpointMarker)._midpointIndex
      }
      this.startHandleDrag(marker, handleType)
    }

    element.addEventListener('mousedown', (e: MouseEvent): void => {
      e.stopPropagation()
      e.preventDefault()
      if (handleType === 'vertex') {
        this.lastVertexClickShift = e.shiftKey
      }
      initDrag()
    })
    element.addEventListener(
      'touchstart',
      (e: TouchEvent): void => {
        e.preventDefault()
        e.stopPropagation()
        initDrag()
      },
      { passive: false }
    )

    if (handleType === 'vertex') {
      const handleEndpointTap = (e: Event): void => {
        e.stopPropagation()
        e.preventDefault()
        const vertexIndex = (marker as VertexMarker)._vertexIndex
        const coords = (this.feature.geometry as GeoJSON.LineString).coordinates

        // Split-pick mode: clicking an interior vertex splits the line there.
        if (this._splitMode) {
          if (vertexIndex > 0 && vertexIndex < coords.length - 1) {
            this.dispatchEvent(
              new CustomEvent('split-at-vertex', {
                detail: { featureId: this.feature.id, vertexIndex },
                bubbles: true,
              })
            )
          }
          return
        }

        if (!this._extendMode) {
          return
        }

        const direction = tripFeatureManager.getExtendDirection()

        if (direction !== null) {
          // Already extending — tapping active endpoint exits
          if (
            (direction === 'prepend' && vertexIndex === 0) ||
            (direction === 'append' && vertexIndex === coords.length - 1)
          ) {
            this.exitExtendMode()
            return
          }
        }

        // Choose direction based on which endpoint was tapped, then hand off to the
        // drawing manager (via cairn-map) so the full create/extend toolbar + snapping
        // drive the extension.
        let chosenDir: 'append' | 'prepend' | null = null
        if (vertexIndex === 0) chosenDir = 'prepend'
        else if (vertexIndex === coords.length - 1) chosenDir = 'append'
        if (chosenDir) {
          tripFeatureManager.setExtendDirection(chosenDir)
          this.dispatchEvent(
            new CustomEvent('extend-begin', {
              detail: { featureId: this.feature.id, direction: chosenDir },
              bubbles: true,
            })
          )
        }
      }

      element.addEventListener('click', handleEndpointTap)
      element.addEventListener('touchend', handleEndpointTap, {
        passive: false,
      } as AddEventListenerOptions)
    }
  }

  protected override handleDrag(
    marker: maplibregl.Marker,
    handleType: string,
    newPosition: maplibregl.LngLat
  ): void {
    if (!this.hasDragMoved && this.dragStartLngLat) {
      const dx = newPosition.lng - this.dragStartLngLat.lng
      const dy = newPosition.lat - this.dragStartLngLat.lat
      if (Math.abs(dx) > 0.00001 || Math.abs(dy) > 0.00001) {
        this.hasDragMoved = true
      }
    }
    super.handleDrag(marker, handleType, newPosition)
  }

  protected updateShapePreview(handleType: string, newPosition: maplibregl.LngLat): void {
    if (handleType === 'vertex' && this.draggedVertexIndex !== undefined) {
      const geom = this.feature.geometry as GeoJSON.LineString
      const coords = updateVertexPosition(
        geom.coordinates,
        this.draggedVertexIndex,
        newPosition.lng,
        newPosition.lat
      )
      geom.coordinates = coords

      const source = this.map.getSource(this.sourceId)
      if (isGeoJSONSource(source)) {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: coords },
        })
      }

      this.updateMidpointHandlesForVertex(this.draggedVertexIndex)
    } else if (handleType === 'midpoint' && this.draggedMidpointIndex !== undefined) {
      const coords = insertMidpointVertex(
        (this.feature.geometry as GeoJSON.LineString).coordinates,
        this.draggedMidpointIndex,
        newPosition.lng,
        newPosition.lat
      )

      const source = this.map.getSource(this.sourceId)
      if (isGeoJSONSource(source)) {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: coords },
        })
      }
    }
  }

  protected async applyGeometryChange(
    handleType: string,
    newPosition: maplibregl.LngLat
  ): Promise<void> {
    if (handleType === 'vertex' && this.draggedVertexIndex !== undefined) {
      if (!this.hasDragMoved) {
        // Click (no drag) — toggle selection
        if (this.lastVertexClickShift) {
          const newSet = new Set(this.selectedVertexIndices)
          if (newSet.has(this.draggedVertexIndex)) {
            newSet.delete(this.draggedVertexIndex)
          } else {
            newSet.add(this.draggedVertexIndex)
          }
          this.selectedVertexIndices = newSet
        } else {
          this.selectedVertexIndices = new Set([this.draggedVertexIndex])
        }
        this.draggedVertexIndex = undefined
        this.dispatchVertexSelectionChanged()
        return
      }

      const coords = updateVertexPosition(
        (this.feature.geometry as GeoJSON.LineString).coordinates,
        this.draggedVertexIndex,
        newPosition.lng,
        newPosition.lat
      )
      const newGeometry: GeoJSON.LineString = { type: 'LineString', coordinates: coords }
      await db.features.update(this.feature.id, {
        geometry: newGeometry,
        updatedAt: new Date().toISOString(),
      })
      this.feature.geometry = newGeometry
      this.selectedVertexIndices = new Set([this.draggedVertexIndex])
      this.draggedVertexIndex = undefined
    } else if (handleType === 'midpoint' && this.draggedMidpointIndex !== undefined) {
      const coords = insertMidpointVertex(
        (this.feature.geometry as GeoJSON.LineString).coordinates,
        this.draggedMidpointIndex,
        newPosition.lng,
        newPosition.lat
      )
      const newGeometry: GeoJSON.LineString = { type: 'LineString', coordinates: coords }
      await db.features.update(this.feature.id, {
        geometry: newGeometry,
        updatedAt: new Date().toISOString(),
      })
      this.feature.geometry = newGeometry
      this.draggedMidpointIndex = undefined
    }
  }

  protected updateAllHandlePositions(_centerPosition: maplibregl.LngLat): void {
    const coords = (this.feature.geometry as GeoJSON.LineString).coordinates
    this.midpointHandles.forEach((handle, index) => {
      if (index < coords.length - 1) {
        handle.setLngLat(calculateMidpoint(coords[index], coords[index + 1]))
      }
    })
  }

  private updateMidpointHandlesForVertex(vertexIndex: number): void {
    const coords = (this.feature.geometry as GeoJSON.LineString).coordinates
    if (vertexIndex > 0) {
      const prevMidpointIndex = vertexIndex - 1
      if (prevMidpointIndex < this.midpointHandles.length) {
        this.midpointHandles[prevMidpointIndex].setLngLat(
          calculateMidpoint(coords[vertexIndex - 1], coords[vertexIndex])
        )
      }
    }
    if (vertexIndex < coords.length - 1) {
      if (vertexIndex < this.midpointHandles.length) {
        this.midpointHandles[vertexIndex].setLngLat(
          calculateMidpoint(coords[vertexIndex], coords[vertexIndex + 1])
        )
      }
    }
  }

  protected override applySelectionStyle(selected: boolean): void {
    const lineLayerId = `${this.sourceId}-line`
    if (this.map.getLayer(lineLayerId)) {
      this.map.setPaintProperty(
        lineLayerId,
        'line-width',
        selected ? (this.feature.style?.lineWidth ?? 3) + 2 : (this.feature.style?.lineWidth ?? 3)
      )
    }
  }

  private applySelectedVertexStyle(handle: maplibregl.Marker): void {
    const el = handle.getElement()
    el.style.background = '#3b82f6'
    el.style.border = '2px solid #1d4ed8'
    el.style.boxShadow = '0 0 6px rgba(59,130,246,0.7)'
    el.style.width = '12px'
    el.style.height = '12px'
  }

  hasSelectedVertex(): boolean {
    return this.selectedVertexIndices.size > 0 && this.isEditing
  }

  canSplit(): boolean {
    if (this.selectedVertexIndices.size === 0 || !this.isEditing) return false
    const coords = (this.feature.geometry as GeoJSON.LineString).coordinates
    return computeSplitSegments(coords.length, this.selectedVertexIndices) !== null
  }

  getSelectedVertexIndex(): number | undefined {
    if (this.selectedVertexIndices.size !== 1) return undefined
    return this.selectedVertexIndices.values().next().value as number
  }

  getSelectedVertexIndices(): Set<number> {
    return this.selectedVertexIndices
  }

  selectVerticesInBounds(bounds: {
    west: number
    east: number
    south: number
    north: number
  }): void {
    const coords = (this.feature.geometry as GeoJSON.LineString).coordinates
    this.selectedVertexIndices = new Set()
    for (let i = 0; i < coords.length; i++) {
      const [lng, lat] = coords[i]
      if (lng >= bounds.west && lng <= bounds.east && lat >= bounds.south && lat <= bounds.north) {
        this.selectedVertexIndices.add(i)
      }
    }
    this.rebuildVisibleHandles()
    this.dispatchVertexSelectionChanged()
  }

  async deleteSelectedVertices(): Promise<void> {
    if (this.selectedVertexIndices.size === 0) return

    const coords = (this.feature.geometry as GeoJSON.LineString).coordinates
    const newCoords = deleteVerticesFromCoords(coords, this.selectedVertexIndices)
    if (!newCoords) return

    const newProps = filterMetadataArrays(this.feature.properties ?? {}, this.selectedVertexIndices)
    const newGeometry: GeoJSON.LineString = { type: 'LineString', coordinates: newCoords }
    await db.features.update(this.feature.id, {
      geometry: newGeometry,
      properties: newProps,
      updatedAt: new Date().toISOString(),
    })
    this.feature.geometry = newGeometry
    this.feature.properties = newProps
    this.selectedVertexIndices = new Set()

    this.updateSource()
    this.removeHandles()
    this.createHandles()
    this.dispatchVertexSelectionChanged()
  }

  /** Append a reversed copy of the coordinates to create an out-and-back route */
  async appendReturn(): Promise<void> {
    const coords = (this.feature.geometry as GeoJSON.LineString).coordinates
    // Reverse all but the last point (which is already the end) to avoid duplicate
    const reversed = [...coords].reverse().slice(1)
    const newCoords = [...coords, ...reversed]
    const newGeometry: GeoJSON.LineString = { type: 'LineString', coordinates: newCoords }
    await db.features.update(this.feature.id, {
      geometry: newGeometry,
      updatedAt: new Date().toISOString(),
    })
    this.feature.geometry = newGeometry
    this.updateSource()
    if (this.isEditing) {
      this.removeHandles()
      this.createHandles()
    }
  }

  override enterEditMode(): void {
    this.selectedVertexIndices = new Set()
    super.enterEditMode()
  }

  override exitEditMode(): void {
    this.selectedVertexIndices = new Set()
    this._extendMode = false
    this._splitMode = false
    super.exitEditMode()
  }

  /** Show just the two endpoint handles so an end can be picked for extending — without
   *  the old trip-feature-manager extend state (the drawing manager owns the session). */
  showExtendEndpoints(): void {
    this._extendMode = true
    if (!this.isEditing) this.enterEditMode()
    this.updateHandlesForExtendMode()
  }

  /** Enter split-pick mode: keep the vertex handles visible but non-draggable, so a
   *  click on a vertex is a tap that picks the split point. */
  enterSplitMode(): void {
    this._splitMode = true
    if (!this.isEditing) this.enterEditMode()
    // Cue that handles are click-to-split rather than drag.
    this.handles.forEach(h => h.getElement().style.setProperty('cursor', 'pointer', 'important'))
  }

  /** Leave split-pick mode (re-enable dragging) without leaving edit mode. */
  exitSplitMode(): void {
    this._splitMode = false
    this.handles.forEach(h => h.getElement().style.setProperty('cursor', 'grab', 'important'))
  }

  private dispatchVertexSelectionChanged(): void {
    this.dispatchEvent(
      new CustomEvent('vertex-selection-changed', {
        detail: {
          featureId: this.feature.id,
          count: this.selectedVertexIndices.size,
          indices: [...this.selectedVertexIndices],
        },
        bubbles: true,
      })
    )
  }

  protected override removeHandles(): void {
    if (this._boundZoomHandler) {
      this.map.off('zoomend', this._boundZoomHandler)
      this.map.off('moveend', this._boundZoomHandler)
      this._boundZoomHandler = undefined
    }
    this.vertexHandles = []
    this.midpointHandles = []
    super.removeHandles()
  }

  override removeFromMap(): void {
    if ((this.map as unknown as Record<string, unknown>).style) {
      this.removeTracerLine()
    }
    super.removeFromMap()
  }

  private updateHandlesForExtendMode(): void {
    if (!this.isEditing) {
      return
    }
    this.removeHandles()
    this.showEndpointHandlesOnly()
  }

  /**
   * Show only the first and last vertex as handles.
   * Clicking one sets the extend direction; clicking the active one exits.
   */
  private showEndpointHandlesOnly(): void {
    // Remove existing handles from DOM, then clear arrays
    this.handles.forEach(h => h.remove())
    this.vertexHandles = []
    this.midpointHandles = []
    this.handles = []

    const coords = (this.feature.geometry as GeoJSON.LineString).coordinates
    if (coords.length === 0) {
      return
    }

    const direction = tripFeatureManager.getExtendDirection()

    // Create first endpoint handle
    const firstCoord = coords[0]
    const firstHandle = this.createHandle([firstCoord[0], firstCoord[1]], 'vertex')
    ;(firstHandle as VertexMarker)._vertexIndex = 0
    this.vertexHandles.push(firstHandle)
    this.handles.push(firstHandle)

    // Style active endpoint differently
    if (direction === 'prepend') {
      firstHandle.getElement().style.background = '#22c55e'
      firstHandle.getElement().style.boxShadow = '0 0 6px #22c55e'
    }

    // Create last endpoint handle (only if more than 1 point)
    if (coords.length > 1) {
      const lastCoord = coords[coords.length - 1]
      const lastHandle = this.createHandle([lastCoord[0], lastCoord[1]], 'vertex')
      ;(lastHandle as VertexMarker)._vertexIndex = coords.length - 1
      this.vertexHandles.push(lastHandle)
      this.handles.push(lastHandle)

      if (direction === 'append') {
        lastHandle.getElement().style.background = '#22c55e'
        lastHandle.getElement().style.boxShadow = '0 0 6px #22c55e'
      }
    }
  }

  private showAllHandles(): void {
    this.removeHandles()
    this.createHandles()
  }

  private updateTracerLine(): void {
    if (
      !tripFeatureManager.isActivelyExtending() ||
      tripFeatureManager.getExtendingFeatureId() !== this.feature.id
    ) {
      this.removeTracerLine()
      return
    }
    if (!this.lastMousePosition) {
      this.removeTracerLine()
      return
    }

    const coords = (this.feature.geometry as GeoJSON.LineString).coordinates
    const direction = tripFeatureManager.getExtendDirection()
    const anchorCoord = direction === 'prepend' ? coords[0] : coords[coords.length - 1]
    const tracerGeoJSON: GeoJSON.Feature = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: [anchorCoord, [this.lastMousePosition.lng, this.lastMousePosition.lat]],
      },
    }

    if (!this.tracerSourceId) {
      this.tracerSourceId = `${this.sourceId}-tracer`
      this.tracerLayerId = `${this.sourceId}-tracer-layer`
      this.map.addSource(this.tracerSourceId, { type: 'geojson', data: tracerGeoJSON })
      addShapeLayer(this.map, {
        id: this.tracerLayerId,
        type: 'line',
        source: this.tracerSourceId,
        paint: {
          'line-color': this.feature.style?.lineColor ?? '#3f5a7a',
          'line-width': this.feature.style?.lineWidth ?? 3,
          'line-opacity': 0.5,
          'line-dasharray': [5, 5],
        },
      })
    } else {
      const source = this.map.getSource(this.tracerSourceId)
      if (isGeoJSONSource(source)) {
        source.setData(tracerGeoJSON)
      }
    }
  }

  private removeTracerLine(): void {
    if (this.tracerLayerId && this.map.getLayer(this.tracerLayerId)) {
      this.map.removeLayer(this.tracerLayerId)
    }
    if (this.tracerSourceId && this.map.getSource(this.tracerSourceId)) {
      this.map.removeSource(this.tracerSourceId)
    }
    this.tracerSourceId = undefined
    this.tracerLayerId = undefined
  }

  enterExtendMode(): void {
    tripFeatureManager.setExtendMode(this.feature.id)
    if (!this.isEditing) {
      this.enterEditMode()
    }
    // Show only the two endpoints — user clicks one to choose direction
    this.updateHandlesForExtendMode()
  }

  exitExtendMode(): void {
    this.removeTracerLine()
    tripFeatureManager.exitExtendMode()
    this.showAllHandles()
  }
}
